import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchInfluencers, deleteInfluencer, getUser, generateConnectLink, fetchIGData, updateInfluencer, generateFormLink } from '../api';
import { PLATFORMS, TIERS, NICHES, TIER_COLORS, fmtK, fmtPct, fmtRate, S, CHORD } from '../constants';
import InfluencerForm from '../components/InfluencerForm';
import StatsPanel from '../components/StatsPanel';

const RATE_TYPES = ['stories','reels','shoot','static','affiliate'];
const EMPTY_FILTERS = { name:'', handle:'', tier:'', niche:'', city:'', state:'', country:'', language:'', relationship:'', min_followers:0, rate_type:'', brands:'' };

// Auto-synced fields — not editable inline
const AUTO_FIELDS = new Set(['followers','avg_views','engagement_rate','reach','post_count_30d','video_count_30d','ig_access_token','ig_account_id','added_by','added_by_name','created_at','updated_at','id']);

function Pills({ value, color = 'rgba(245,235,221,0.5)' }) {
  if (!value) return <span style={{ color:'rgba(245,235,221,0.2)' }}>—</span>;
  const items = value.split(',').map(s => s.trim()).filter(Boolean);
  if (!items.length) return <span style={{ color:'rgba(245,235,221,0.2)' }}>—</span>;
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
      {items.map((item,i) => (
        <span key={i} style={{ background:`${color}18`, border:`0.5px solid ${color}40`, color, borderRadius:20, padding:'1px 7px', fontSize:10, fontWeight:500, whiteSpace:'nowrap' }}>{item}</span>
      ))}
    </div>
  );
}

// ── Editable Cell ─────────────────────────────────────────────────────────
function EditableCell({ value, field, infId, platform, onSaved, isAuto, isRate, tdStyle, tdClassName, children }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState(value ?? '');
  const [saving, setSaving]   = useState(false);
  const inputRef              = useRef(null);

  useEffect(() => { setVal(value ?? ''); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  if (isAuto) {
    return <td style={tdStyle} className={tdClassName}>{children}</td>;
  }

  async function save() {
    if (val === (value ?? '')) { setEditing(false); return; }
    setSaving(true);
    try {
      await updateInfluencer(platform, infId, { [field]: val === '' ? null : val });
      onSaved(infId, field, val === '' ? null : val);
    } catch(e) { alert(e.message); setVal(value ?? ''); }
    finally { setSaving(false); setEditing(false); }
  }

  function handleKey(e) {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') { setVal(value ?? ''); setEditing(false); }
  }

  const cellStyle = {
    ...tdStyle,
    cursor: 'pointer',
    position: tdStyle.position || 'relative',
    padding: editing ? '0' : tdStyle.padding,
  };

  const inpStyle = {
    width: '100%', height: '100%',
    background: '#2B2B1F',
    border: `1.5px solid ${CHORD.coral}`,
    borderRadius: 4,
    padding: '6px 8px',
    fontSize: 12,
    color: CHORD.beige,
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    minWidth: 120,
  };

  return (
    <td style={cellStyle} className={tdClassName}
      onClick={() => !editing && setEditing(true)}
      title={isAuto ? '' : 'Click to edit'}
    >
      {editing ? (
        <input
          ref={inputRef}
          style={inpStyle}
          value={val}
          onChange={e => setVal(e.target.value)}
          onBlur={save}
          onKeyDown={handleKey}
          disabled={saving}
        />
      ) : (
        <div style={{ display:'flex', alignItems:'center', gap:4, minWidth:60 }}>
          <span style={{ flex:1 }}>{children || <span style={{ color:'rgba(245,235,221,0.2)' }}>—</span>}</span>
          {!saving && (
            <span style={{ fontSize:9, opacity:0, transition:'opacity 0.15s', color: CHORD.coral }}
              className="cell-pencil">✏</span>
          )}
          {saving && <span style={{ fontSize:9, color: CHORD.coral }}>…</span>}
        </div>
      )}
    </td>
  );
}

// ── Select Cell (for tier, niche, brand_fit) ──────────────────────────────
function SelectCell({ value, field, infId, platform, onSaved, options, tdStyle, renderValue }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState(value ?? '');
  const selectRef             = useRef(null);

  useEffect(() => { setVal(value ?? ''); }, [value]);
  useEffect(() => { if (editing && selectRef.current) selectRef.current.focus(); }, [editing]);

  async function save(newVal) {
    setEditing(false);
    if (newVal === (value ?? '')) return;
    try {
      await updateInfluencer(platform, infId, { [field]: newVal === '' ? null : newVal });
      onSaved(infId, field, newVal === '' ? null : newVal);
    } catch(e) { alert(e.message); setVal(value ?? ''); }
  }

  return (
    <td style={{ ...tdStyle, cursor:'pointer', position:'relative' }} onClick={() => !editing && setEditing(true)}>
      {editing ? (
        <select
          ref={selectRef}
          style={{ background:'#2B2B1F', border:`1.5px solid ${CHORD.coral}`, borderRadius:4, padding:'5px 8px', fontSize:12, color: CHORD.beige, outline:'none', fontFamily:'inherit', width:'100%' }}
          value={val}
          onChange={e => { setVal(e.target.value); save(e.target.value); }}
          onBlur={() => setEditing(false)}
          autoFocus
        >
          <option value="">—</option>
          {options.map(o => <option key={o} value={o} style={{background:'#2B2B1F'}}>{o}</option>)}
        </select>
      ) : (
        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
          <span style={{ flex:1 }}>{renderValue ? renderValue(val) : (val || <span style={{color:'rgba(245,235,221,0.2)'}}>—</span>)}</span>
          <span style={{ fontSize:9, opacity:0, color: CHORD.coral }} className="cell-pencil">✏</span>
        </div>
      )}
    </td>
  );
}

// ── Sticky Scrollbar ──────────────────────────────────────────────────────
function StickyScrollBar({ tableRef }) {
  const stickyRef  = useRef(null);
  const innerRef   = useRef(null);
  const syncingRef = useRef(false);

  useEffect(() => {
    const table  = tableRef.current;
    const sticky = stickyRef.current;
    const inner  = innerRef.current;
    if (!table || !sticky || !inner) return;

    function updateWidth() {
      const tableEl = table.querySelector('table');
      const w = tableEl ? tableEl.offsetWidth : table.scrollWidth;
      inner.style.width = w + 'px';
      // Also sync sticky width to table container width
      sticky.style.width = table.offsetWidth + 'px';
    }

    updateWidth();
    const t1 = setTimeout(updateWidth, 100);
    const t2 = setTimeout(updateWidth, 500);
    const t3 = setTimeout(updateWidth, 1500);

    function onTableScroll() {
      if (syncingRef.current) return;
      syncingRef.current = true;
      sticky.scrollLeft = table.scrollLeft;
      syncingRef.current = false;
    }
    function onStickyScroll() {
      if (syncingRef.current) return;
      syncingRef.current = true;
      table.scrollLeft = sticky.scrollLeft;
      syncingRef.current = false;
    }

    table.addEventListener('scroll', onTableScroll);
    sticky.addEventListener('scroll', onStickyScroll);
    window.addEventListener('resize', updateWidth);

    let observer;
    if (window.ResizeObserver) {
      observer = new ResizeObserver(updateWidth);
      observer.observe(table);
      const tableEl = table.querySelector('table');
      if (tableEl) observer.observe(tableEl);
    }

    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      table.removeEventListener('scroll', onTableScroll);
      sticky.removeEventListener('scroll', onStickyScroll);
      window.removeEventListener('resize', updateWidth);
      if (observer) observer.disconnect();
    };
  }, [tableRef]);

  return (
    <div className="sheet-sticky-scroll" ref={stickyRef}>
      <div className="sheet-sticky-inner" ref={innerRef} />
    </div>
  );
}

export default function PlatformPage() {
  const { platformKey } = useParams();
  const platformInfo    = PLATFORMS.find(p => p.key === platformKey);
  const [tab, setTab]   = useState('search');
  const [editData, setEditData] = useState(null);

  if (!platformInfo) return <div style={{ padding:'2rem', color:'#E24B4A' }}>Unknown platform</div>;

  function handleEdit(inf) { setEditData(inf); setTab('add'); }
  function handleSaved()   { setEditData(null); setTab('search'); }

  const tabBtn = (key, label) => (
    <button onClick={() => { setTab(key); if (key === 'search') setEditData(null); }} style={{
      background:'none', border:'none',
      borderBottom: `2px solid ${tab === key ? platformInfo.color : 'transparent'}`,
      cursor:'pointer', fontFamily:'inherit', fontSize:13, padding:'8px 18px',
      color: tab === key ? platformInfo.color : 'rgba(245,235,221,0.4)',
      fontWeight: tab === key ? 600 : 400,
    }}>{label}</button>
  );

  return (
    <div>
      <div style={{ background: CHORD.softBlack, padding:'1rem 2rem 0', borderBottom:`1px solid ${CHORD.border}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
          <span style={{ width:8, height:8, borderRadius:'50%', background:platformInfo.color, display:'inline-block' }}/>
          <span style={{ fontSize:18, fontWeight:600 }}>{platformInfo.label}</span>
        </div>
        <div style={{ fontSize:12, color:'rgba(245,235,221,0.35)', marginBottom:'0.75rem' }}>Manage {platformInfo.label} influencers</div>
        <div style={{ display:'flex' }}>
          {tabBtn('search','Search / Sheet')}
          {tabBtn('stats','Stats')}
          {tabBtn('add', editData ? `Edit — ${editData.influencer_name}` : 'Add Influencer')}
        </div>
      </div>

      {tab === 'search'   && <SearchSheet platform={platformKey} platformInfo={platformInfo} onEdit={handleEdit} />}
      {tab === 'stats'    && <StatsPanel platform={platformKey} />}
      {tab === 'add'      && <InfluencerForm platform={platformKey} platformLabel={platformInfo.label} editData={editData} onSaved={handleSaved} onCancel={() => { setEditData(null); setTab('search'); }} />}
    </div>
  );
}

// All columns definition — used for visibility toggle
const ALL_COLUMNS = [
  { key:'influencer_name', label:'Name',              group:'basic',   default:true },
  { key:'handle',          label:'Handle / URL',       group:'basic',   default:true },
  { key:'tier',            label:'Tier',               group:'basic',   default:true },
  { key:'niche',           label:'Niche',              group:'basic',   default:true },
  { key:'audience',        label:'Audience',           group:'basic',   default:true },
  { key:'city',            label:'City',               group:'location',default:true },
  { key:'state',           label:'State',              group:'location',default:true },
  { key:'country',         label:'Country',            group:'location',default:true },
  { key:'language',        label:'Language',           group:'basic',   default:true },
  { key:'followers',       label:'Followers',          group:'metrics', default:true },
  { key:'avg_views',       label:'Avg Views/Post',     group:'metrics', default:true },
  { key:'engagement_rate', label:'Eng Rate',           group:'metrics', default:true },
  { key:'reach',           label:'Avg Reach/Post',     group:'metrics', default:true },
  { key:'post_count_30d',  label:'Posts (30d)',        group:'metrics', default:true },
  { key:'video_count_30d', label:'Videos (30d)',       group:'metrics', default:true },
  { key:'rate_stories',    label:'Stories ₹',          group:'rates',   default:true },
  { key:'rate_reels',      label:'Reels ₹',            group:'rates',   default:true },
  { key:'rate_shoot',      label:'Shoot ₹',            group:'rates',   default:true },
  { key:'rate_static',     label:'Static ₹',           group:'rates',   default:true },
  { key:'rate_affiliate',  label:'Affiliate ₹',        group:'rates',   default:true },
  { key:'previous_collab', label:'Past Collabs',       group:'collab',  default:true },
  { key:'brands',          label:'Brands',             group:'collab',  default:true },
  { key:'email_contact',   label:'Contact',            group:'collab',  default:true },
  { key:'agency_manager',  label:'Agency / Manager',   group:'collab',  default:true },
  { key:'rate',            label:'Base Rate ₹',        group:'collab',  default:true },
  { key:'preferred_deliverables', label:'Deliverables',group:'collab',  default:true },
  { key:'brand_fit',       label:'Brand Fit',          group:'collab',  default:true },
  { key:'relationship_status', label:'Relationship',   group:'collab',  default:true },
  { key:'notes',           label:'Notes',              group:'collab',  default:true },
];

const DEFAULT_VISIBLE = new Set(ALL_COLUMNS.map(c=>c.key));

function SearchSheet({ platform, platformInfo, onEdit }) {
  const navigate              = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [sortCol, setSortCol] = useState('influencer_name');
  const [sortDir, setSortDir] = useState('asc');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const debounceRef           = useRef(null);
  const tableScrollRef        = useRef(null);

  // Column visibility
  const [visibleCols, setVisibleCols] = useState(DEFAULT_VISIBLE);
  const [showColPanel, setShowColPanel] = useState(false);

  // Saved filters
  const [savedFilters, setSavedFilters] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`saved_filters_${platform}`) || '[]'); } catch { return []; }
  });
  const [showSavePanel, setShowSavePanel] = useState(false);
  const [saveFilterName, setSaveFilterName] = useState('');

  const load = useCallback(async (f) => {
    setLoading(true); setError('');
    try { const res = await fetchInfluencers(platform, f); setRows(res.data); }
    catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [platform]);

  useEffect(() => { setFilters(EMPTY_FILTERS); load({}); }, [platform, load]);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(filters), 300);
    return () => clearTimeout(debounceRef.current);
  }, [filters, load]);

  function setF(k,v) { setFilters(prev => ({...prev,[k]:v})); }
  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d==='asc'?'desc':'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  // Column visibility
  function toggleCol(key) {
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  function showAllCols()     { setVisibleCols(new Set(ALL_COLUMNS.map(c=>c.key))); }
  function resetColsDefault() { setVisibleCols(DEFAULT_VISIBLE); }

  // Saved filters
  function saveCurrentFilter() {
    if (!saveFilterName.trim()) return;
    const newSaved = [...savedFilters, { name: saveFilterName.trim(), filters }];
    setSavedFilters(newSaved);
    localStorage.setItem(`saved_filters_${platform}`, JSON.stringify(newSaved));
    setSaveFilterName('');
    setShowSavePanel(false);
  }
  function loadSavedFilter(sf) {
    setFilters(sf.filters);
    setShowSavePanel(false);
  }
  function deleteSavedFilter(i) {
    const newSaved = savedFilters.filter((_,idx)=>idx!==i);
    setSavedFilters(newSaved);
    localStorage.setItem(`saved_filters_${platform}`, JSON.stringify(newSaved));
  }

  // Update a single cell in the rows array without refetching
  function handleCellSaved(id, field, newVal) {
    setRows(prev => prev.map(r => r.id === id ? {...r, [field]: newVal} : r));
  }

  const sorted = [...rows].sort((a,b) => {
    let av=a[sortCol]??'', bv=b[sortCol]??'';
    if (typeof av==='number') return sortDir==='asc'?av-bv:bv-av;
    return sortDir==='asc'?String(av).localeCompare(String(bv)):String(bv).localeCompare(String(av));
  });

  async function handleDelete(id, name) {
    if (!window.confirm(`Delete ${name}?`)) return;
    try { await deleteInfluencer(platform, id); setRows(prev => prev.filter(r => r.id !== id)); }
    catch(e) { alert(e.message); }
  }

  async function handleGenerateLink(inf) {
    try {
      const data = await generateConnectLink(platform, inf.id);
      await navigator.clipboard.writeText(data.connect_url);
      alert(`Connect link copied!\n\nSend to ${inf.influencer_name}:\n${data.connect_url}`);
    } catch(e) { alert('Failed: ' + e.message); }
  }

  async function handleFetchIG(inf) {
    if (!window.confirm(`Fetch live Instagram data for ${inf.influencer_name}?`)) return;
    try {
      const data = await fetchIGData(platform, inf.id);
      alert(`✓ Updated!\nFollowers: ${data.followers?.toLocaleString()}\nEngagement: ${(data.engagement_rate*100).toFixed(2)}%`);
      load(filters);
    } catch(e) { alert('Failed: ' + e.message); }
  }

  // ── CSV Export ───────────────────────────────────────────────────────────
  function exportCSV() {
    const COLS = [
      'influencer_name','handle','tier','niche','audience','city','state','country','language',
      'followers','avg_views','engagement_rate','reach','post_count_30d','video_count_30d',
      'rate','rate_stories','rate_reels','rate_shoot','rate_static','rate_affiliate',
      'previous_collab','email_contact','agency_manager','preferred_deliverables',
      'brand_fit','relationship_status','notes','brands',
    ];
    const HEADERS = [
      'Name','Handle','Tier','Niche','Audience','City','State','Country','Language',
      'Followers','Avg Views','Engagement Rate','Reach/Post','Posts 30d','Videos 30d',
      'Rate','Rate Stories','Rate Reels','Rate Shoot','Rate Static','Rate Affiliate',
      'Prev Collab','Email/Contact','Agency','Deliverables',
      'Brand Fit','Relationship','Notes',
    ];

    function esc(v) {
      if (v === null || v === undefined || v === '') return '';
      const s = String(v);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g,'""')}"`;
      return s;
    }

    const rows = [
      HEADERS.join(','),
      ...sorted.map(inf => COLS.map(c => esc(inf[c])).join(',')),
    ];

    const blob = new Blob(['\uFEFF' + rows.join('\n')], { type:'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `chord_${platformInfo.label.toLowerCase()}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Bulk Import from Excel/CSV ───────────────────────────────────────────
  const importRef = useRef(null);
  const [importing, setImporting]   = useState(false);
  const [importResult, setImportResult] = useState(null);

  const [importModal, setImportModal] = useState(null); // { duplicates, newRows, fieldMap }

  async function handleImportFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    setImporting(true);
    setImportResult(null);

    try {
      const text = await readFileAsText(file);
      const csvRows = parseCSV(text);

      if (csvRows.length < 2) { setImportResult({ error:'File appears empty or has no data rows.' }); setImporting(false); return; }

      const headers = csvRows[0].map(h => h.trim().toLowerCase().replace(/[\s\/\(\)]+/g,'_'));
      const dataRows = csvRows.slice(1).filter(r => r.some(c => c.trim()));

      const fieldMap = {
        'name':'influencer_name','influencer_name':'influencer_name',
        'handle':'handle','profile_url':'handle','url':'handle',
        'tier':'tier','niche':'niche','category':'niche',
        'audience':'audience','city':'city','state':'state','country':'country',
        'language':'language','languages':'language',
        'followers':'followers','avg_views':'avg_views','average_views':'avg_views',
        'engagement_rate':'engagement_rate','engagement':'engagement_rate',
        'reach':'reach','reach_post':'reach',
        'posts_30d':'post_count_30d','posts':'post_count_30d',
        'videos_30d':'video_count_30d','videos':'video_count_30d',
        'rate':'rate','rate_stories':'rate_stories','rate_reels':'rate_reels',
        'rate_shoot':'rate_shoot','rate_static':'rate_static','rate_affiliate':'rate_affiliate',
        'previous_collab':'previous_collab','prev_collab':'previous_collab','previous_collaboration':'previous_collab',
        'email_contact':'email_contact','email':'email_contact','contact':'email_contact',
        'agency':'agency_manager','agency_manager':'agency_manager',
        'deliverables':'preferred_deliverables','preferred_deliverables':'preferred_deliverables',
        'brand_fit':'brand_fit','relationship':'relationship_status','relationship_status':'relationship_status',
        'notes':'notes','past_work':'past_work',
      };

      // Build payload rows
      const parsedRows = [];
      dataRows.forEach((row, i) => {
        const payload = {};
        headers.forEach((h, idx) => {
          const field = fieldMap[h];
          if (field) payload[field] = row[idx]?.trim() || null;
        });
        if (!payload.influencer_name) return;

        ['followers','avg_views','reach','post_count_30d','video_count_30d'].forEach(k => {
          if (payload[k]) payload[k] = parseInt(payload[k].replace(/[,₹\s]/g,'')) || null;
        });
        ['engagement_rate','rate','rate_stories','rate_reels','rate_shoot','rate_static','rate_affiliate'].forEach(k => {
          if (payload[k]) payload[k] = parseFloat(payload[k].replace(/[,₹\s]/g,'')) || null;
        });
        parsedRows.push({ rowNum: i + 2, payload });
      });

      // Detect duplicates by name (case-insensitive match against current sheet)
      const existingByName = new Map(rows.map(r => [r.influencer_name.trim().toLowerCase(), r]));
      const duplicates = [];
      const newRows = [];

      parsedRows.forEach(pr => {
        const key = pr.payload.influencer_name.trim().toLowerCase();
        if (existingByName.has(key)) {
          duplicates.push({ ...pr, existing: existingByName.get(key) });
        } else {
          newRows.push(pr);
        }
      });

      setImporting(false);

      if (duplicates.length > 0) {
        // Show modal asking what to do with duplicates
        setImportModal({ duplicates, newRows });
      } else {
        // No duplicates — import everything directly
        await runImport(newRows, []);
      }
    } catch(e) {
      setImportResult({ error: e.message });
      setImporting(false);
    }
  }

  async function runImport(newRows, duplicateRows, mode) {
    setImporting(true);
    let added = 0, updated = 0, skipped = 0;
    const errors = [];
    const { bulkImport } = await import('../api');

    try {
      // Send all new rows as single bulk request — logs as one activity entry
      if (newRows.length > 0) {
        const res = await bulkImport(platform, newRows.map(r => ({ payload: r.payload })), 'add');
        added   = res.added   || 0;
        skipped = res.errors?.length || 0;
        errors.push(...(res.errors || []));
      }

      // Handle duplicates
      if (duplicateRows.length > 0 && mode && mode !== 'retain') {
        const res = await bulkImport(platform, duplicateRows.map(r => ({ payload: r.payload, exist_id: r.existing.id })), mode);
        updated  = res.updated || 0;
        skipped += res.errors?.length || 0;
        errors.push(...(res.errors || []));
      } else if (mode === 'retain') {
        skipped += duplicateRows.length;
      }
    } catch(e) {
      errors.push(e.message);
    }

    setImportResult({ added, updated, skipped, errors, total: newRows.length + duplicateRows.length });
    setImportModal(null);
    if (added > 0 || updated > 0) load(filters);
    setImporting(false);
  }

  function readFileAsText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = e => resolve(e.target.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file, 'UTF-8');
    });
  }

  function parseCSV(text) {
    const rows = [];
    const lines = text.replace(/\r\n/g,'\n').replace(/\r/g,'\n').split('\n');
    for (const line of lines) {
      if (!line.trim()) continue;
      const cols = [];
      let cur = '', inQuote = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuote && line[i+1] === '"') { cur += '"'; i++; }
          else inQuote = !inQuote;
        } else if (ch === ',' && !inQuote) {
          cols.push(cur); cur = '';
        } else cur += ch;
      }
      cols.push(cur);
      rows.push(cols);
    }
    return rows;
  }

  function exportPDF() {
    const win = window.open('','_blank');
    const tierCounts = ['Barter','Nano','Micro','Macro'].map(t=>({t,c:sorted.filter(r=>r.tier===t).length})).filter(x=>x.c>0);
    const engAvg = sorted.filter(r=>r.engagement_rate).length > 0
      ? (sorted.reduce((s,r)=>s+(r.engagement_rate||0),0)/sorted.filter(r=>r.engagement_rate).length*100).toFixed(2)+'%' : '—';
    win.document.write(`<!DOCTYPE html><html><head><title>Chord — ${platformInfo.label}</title>
    <style>*{box-sizing:border-box}body{font-family:sans-serif;padding:24px;color:#111;font-size:11px}
    h1{font-size:18px;font-weight:700;margin:0 0 4px}.meta{font-size:11px;color:#888;margin-bottom:16px}
    .stats{display:flex;gap:12px;margin-bottom:20px;flex-wrap:wrap}
    .stat{background:#f5f5f5;border-radius:8px;padding:10px 18px;text-align:center}
    .stat-val{font-size:22px;font-weight:700;display:block}.stat-lbl{font-size:10px;color:#777;text-transform:uppercase;letter-spacing:.8px}
    table{width:100%;border-collapse:collapse}
    th{background:#1a1a1a;color:#F5EBDD;padding:7px 8px;text-align:left;font-size:10px;letter-spacing:.5px;white-space:nowrap}
    td{padding:6px 8px;border-bottom:1px solid #f0f0f0}tr:nth-child(even) td{background:#fafafa}
    @media print{body{padding:12px}}</style></head><body>
    <h1>Chord Agency — ${platformInfo.label}</h1>
    <div class="meta">Exported ${new Date().toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})} · ${sorted.length} influencers</div>
    <div class="stats">
      <div class="stat"><span class="stat-val">${sorted.length}</span><span class="stat-lbl">Total</span></div>
      ${tierCounts.map(x=>`<div class="stat"><span class="stat-val">${x.c}</span><span class="stat-lbl">${x.t}</span></div>`).join('')}
      <div class="stat"><span class="stat-val">${fmtK(sorted.reduce((s,r)=>s+(r.followers||0),0))}</span><span class="stat-lbl">Total Followers</span></div>
      <div class="stat"><span class="stat-val">${engAvg}</span><span class="stat-lbl">Avg Engagement</span></div>
    </div>
    <table><thead><tr>
      <th>#</th><th>Name</th><th>Handle</th><th>Tier</th><th>Niche</th><th>Audience</th>
      <th>City</th><th>State</th><th>Language</th><th>Followers</th><th>Eng %</th>
      <th>Contact</th><th>Stories ₹</th><th>Reels ₹</th><th>Static ₹</th><th>Shoot ₹</th><th>Affiliate ₹</th>
    </tr></thead><tbody>
    ${sorted.map((inf,i)=>`<tr>
      <td style="color:#999">${i+1}</td>
      <td style="font-weight:600">${inf.influencer_name||''}</td>
      <td style="color:#555;font-size:10px">${inf.handle?inf.handle.replace('https://','').replace('www.','').substring(0,30):'—'}</td>
      <td><span style="background:#eee;padding:1px 6px;border-radius:10px">${inf.tier||''}</span></td>
      <td>${inf.niche||'—'}</td><td style="color:#555">${inf.audience||'—'}</td>
      <td>${inf.city||'—'}</td><td>${inf.state||'—'}</td><td>${inf.language||'—'}</td>
      <td style="font-weight:600">${fmtK(inf.followers)}</td><td>${fmtPct(inf.engagement_rate)}</td>
      <td style="font-size:10px">${inf.email_contact||'—'}</td>
      ${RATE_TYPES.map(rt=>`<td>${inf[`rate_${rt}`]?'₹'+Number(inf[`rate_${rt}`]).toLocaleString('en-IN'):'—'}</td>`).join('')}
    </tr>`).join('')}
    </tbody></table>
    <script>window.onload=()=>window.print();</script></body></html>`);
    win.document.close();
  }

  const hasFilters = Object.entries(filters).some(([k,v])=>k==='min_followers'?v>0:v!=='');
  const SI = ({col}) => sortCol!==col?<span style={{opacity:.2,marginLeft:3}}>↕</span>:<span style={{marginLeft:3,color:platformInfo.color}}>{sortDir==='asc'?'↑':'↓'}</span>;

  const fi    = { background:'rgba(245,235,221,0.05)', border:`0.5px solid ${CHORD.border}`, borderRadius:6, padding:'5px 10px', fontSize:12, color: CHORD.beige, outline:'none', fontFamily:'inherit' };
  const fiSel = { ...fi, background:'#2B2B1F', cursor:'pointer' };

  // Column header styles
  const thEdit = { padding:'8px 10px', fontSize:10, fontWeight:600, color:'rgba(245,235,221,0.45)', textTransform:'uppercase', letterSpacing:.8, textAlign:'left', borderBottom:`0.5px solid ${CHORD.border}`, whiteSpace:'nowrap', cursor:'pointer', userSelect:'none', background:'#242420', position:'sticky', top:0, zIndex:10 };
  const thAuto = { ...thEdit, color:'rgba(29,158,117,0.8)', background:'#1A2018' };
  const thRate = { ...thEdit, color:`rgba(242,149,27,0.8)`, background:'#2A2010' };
  const thName = { ...thEdit, left:0, zIndex:12, boxShadow:'2px 0 6px rgba(0,0,0,0.4)' };

  // Cell styles
  const td     = { padding:'8px 10px', fontSize:12, color: CHORD.beige, borderBottom:`0.5px solid rgba(245,235,221,0.04)`, verticalAlign:'middle', whiteSpace:'nowrap', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis' };
  const tdM    = { ...td, color:'rgba(245,235,221,0.5)', fontSize:11 };
  const tdAuto = { ...td, background:'rgba(29,158,117,0.03)' };
  const tdRate = { ...td, background:'rgba(242,149,27,0.03)', color: CHORD.orange, fontSize:11 };
  const tdName = { ...td, fontWeight:500, position:'sticky', left:0, background:'#1A1A1A', zIndex:2, boxShadow:'2px 0 6px rgba(0,0,0,0.3)' }; // sticky name col

  const activeRateType = filters.rate_type;

  // Props for editable cells
  const ec = (field, inf, style, children) => ({
    field, infId: inf.id, platform,
    onSaved: handleCellSaved,
    isAuto: AUTO_FIELDS.has(field),
    tdStyle: style,
    value: inf[field],
    children,
  });

  return (
    <div>
      {/* Hover pencil CSS */}
      <style>{`
        td:hover .cell-pencil { opacity: 0.6 !important; }
        td:hover { background: rgba(229,93,74,0.04) !important; }
      `}</style>

      {/* Filter bar */}
      <div style={{ background:'#1F1F1B', borderBottom:`0.5px solid ${CHORD.border}`, padding:'0.75rem 2rem' }}>
        {/* Row 1 — filters */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:8, alignItems:'flex-end', marginBottom:8 }}>
          {[{k:'name',p:'Name...'},{k:'handle',p:'Handle...'},{k:'city',p:'City...'},{k:'state',p:'State...'},{k:'country',p:'Country...'},{k:'language',p:'Language...'},{k:'relationship',p:'Relationship...'}].map(({k,p})=>(
            <input key={k} style={{...fi,width:110}} placeholder={p} value={filters[k]} onChange={e=>setF(k,e.target.value)} />
          ))}
          <select style={fiSel} value={filters.tier} onChange={e=>setF('tier',e.target.value)}>
            <option value="">All tiers</option>{TIERS.map(t=><option key={t} style={{background:'#2B2B1F'}}>{t}</option>)}
          </select>
          <select style={fiSel} value={filters.niche} onChange={e=>setF('niche',e.target.value)}>
            <option value="">All niches</option>{NICHES.map(n=><option key={n} style={{background:'#2B2B1F'}}>{n}</option>)}
          </select>
          <input style={{...fi,width:130}} placeholder="🏷 Brand..." value={filters.brands} onChange={e=>setF('brands',e.target.value)} title="Filter by brand collaboration" />
          <select style={{...fiSel, borderColor:activeRateType?`rgba(242,149,27,0.5)`:CHORD.border}} value={filters.rate_type} onChange={e=>setF('rate_type',e.target.value)}>
            <option value="">All rates</option>
            {RATE_TYPES.map(rt=><option key={rt} value={rt} style={{background:'#2B2B1F'}}>{rt.charAt(0).toUpperCase()+rt.slice(1)} only</option>)}
          </select>
          <div style={{ display:'flex', flexDirection:'column', gap:3, minWidth:160 }}>
            <span style={{ fontSize:10, color:'rgba(245,235,221,0.35)' }}>Min followers — <span style={{color:platformInfo.color,fontWeight:600}}>{filters.min_followers===0?'Any':fmtK(filters.min_followers)}</span></span>
            <input type="range" min="0" max="5000000" step="10000" style={{accentColor:platformInfo.color}} value={filters.min_followers} onChange={e=>setF('min_followers',Number(e.target.value))} />
          </div>
          {hasFilters && <button onClick={()=>setFilters(EMPTY_FILTERS)} style={{...S.btnSecondary,padding:'5px 10px',fontSize:11,alignSelf:'flex-end'}}>Reset</button>}
        </div>
        {/* Row 2 — action buttons always visible */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, alignItems:'center' }}>
          <button onClick={exportPDF} style={{ background:`${CHORD.coral}18`, border:`0.5px solid ${CHORD.coral}50`, borderRadius:6, padding:'5px 12px', fontSize:11, color:CHORD.coral, cursor:'pointer', fontFamily:'inherit' }}>⬇ PDF</button>
          <button onClick={exportCSV} style={{ background:`rgba(29,158,117,0.12)`, border:`0.5px solid rgba(29,158,117,0.4)`, borderRadius:6, padding:'5px 12px', fontSize:11, color:'#1D9E75', cursor:'pointer', fontFamily:'inherit' }}>⬇ CSV</button>
          <button onClick={() => importRef.current?.click()} disabled={importing} style={{ background:`rgba(44,124,229,0.12)`, border:`0.5px solid rgba(44,124,229,0.4)`, borderRadius:6, padding:'5px 12px', fontSize:11, color:'#2C7CE5', cursor:'pointer', fontFamily:'inherit', opacity:importing?0.6:1 }}>
            {importing ? '⏳ Importing...' : '⬆ Import CSV'}
          </button>
          <input ref={importRef} type="file" accept=".csv,.txt" style={{ display:'none' }} onChange={handleImportFile} />
          <button onClick={async()=>{
            try {
              const data = await generateFormLink(platform, `Chord Agency — ${platformInfo.label} Form`, 30);
              await navigator.clipboard.writeText(data.form_url);
              alert(`✓ Form link copied!\n\nShare this with influencers:\n${data.form_url}\n\nExpires in 30 days.`);
            } catch(e) { alert('Failed: ' + e.message); }
          }} style={{ background:`rgba(216,224,77,0.1)`, border:`0.5px solid rgba(216,224,77,0.3)`, borderRadius:6, padding:'5px 12px', fontSize:11, color:'#D8E04D', cursor:'pointer', fontFamily:'inherit' }}>
            🔗 Form Link
          </button>
          <div style={{ width:'1px', height:16, background:'rgba(245,235,221,0.1)' }} />
          <button onClick={()=>{ setShowSavePanel(p=>!p); setShowColPanel(false); }}
            style={{ background: showSavePanel?'rgba(44,124,229,0.2)':'rgba(44,124,229,0.08)', border:`0.5px solid rgba(44,124,229,${showSavePanel?'0.6':'0.3'})`, borderRadius:6, padding:'5px 12px', fontSize:11, color:'#2C7CE5', cursor:'pointer', fontFamily:'inherit' }}>
            🔖 Saved Filters {savedFilters.length > 0 && <span style={{ background:'rgba(44,124,229,0.3)', borderRadius:10, padding:'1px 6px', fontSize:10, marginLeft:3 }}>{savedFilters.length}</span>}
          </button>
          <button onClick={()=>{ setShowColPanel(p=>!p); setShowSavePanel(false); }}
            style={{ background: showColPanel?'rgba(216,224,77,0.2)':'rgba(216,224,77,0.08)', border:`0.5px solid rgba(216,224,77,${showColPanel?'0.6':'0.3'})`, borderRadius:6, padding:'5px 12px', fontSize:11, color:'#D8E04D', cursor:'pointer', fontFamily:'inherit' }}>
            ⚙ Columns ({visibleCols.size}/{ALL_COLUMNS.length})
          </button>
        </div>
      </div>

      {/* Saved Filters Panel */}
      {showSavePanel && (
        <div style={{ background:'#1F1F1B', borderBottom:`0.5px solid ${CHORD.border}`, padding:'1rem 2rem' }}>
          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom: savedFilters.length > 0 ? '0.75rem' : 0, flexWrap:'wrap' }}>
            <input
              style={{ background:'rgba(245,235,221,0.06)', border:`0.5px solid ${CHORD.border}`, borderRadius:6, padding:'5px 10px', fontSize:12, color: CHORD.beige, outline:'none', fontFamily:'inherit', width:180 }}
              placeholder="Name this filter set..."
              value={saveFilterName}
              onChange={e=>setSaveFilterName(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&saveCurrentFilter()}
            />
            <button onClick={saveCurrentFilter} style={{ background:`rgba(44,124,229,0.15)`, border:`0.5px solid rgba(44,124,229,0.4)`, borderRadius:6, padding:'5px 12px', fontSize:12, color:'#2C7CE5', cursor:'pointer', fontFamily:'inherit' }}>Save current filters</button>
            <button onClick={()=>{ setFilters(EMPTY_FILTERS); setShowSavePanel(false); }} style={{ background:'none', border:`0.5px solid ${CHORD.border}`, borderRadius:6, padding:'5px 10px', fontSize:11, color:'rgba(245,235,221,0.4)', cursor:'pointer', fontFamily:'inherit' }}>Reset all</button>
          </div>
          {savedFilters.length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
              {savedFilters.map((sf,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:0, background:'rgba(44,124,229,0.1)', border:'0.5px solid rgba(44,124,229,0.3)', borderRadius:20, overflow:'hidden' }}>
                  <button onClick={()=>loadSavedFilter(sf)} style={{ background:'none', border:'none', padding:'4px 12px', fontSize:12, color:'#2C7CE5', cursor:'pointer', fontFamily:'inherit' }}>{sf.name}</button>
                  <button onClick={()=>deleteSavedFilter(i)} style={{ background:'none', border:'none', padding:'4px 8px 4px 0', fontSize:11, color:'rgba(229,93,74,0.6)', cursor:'pointer', fontFamily:'inherit' }}>✕</button>
                </div>
              ))}
            </div>
          )}
          {savedFilters.length === 0 && !saveFilterName && <div style={{ fontSize:11, color:'rgba(245,235,221,0.25)' }}>No saved filters yet — set filters above and save them here</div>}
        </div>
      )}

      {/* Column Visibility Panel */}
      {showColPanel && (
        <div style={{ background:'#1F1F1B', borderBottom:`0.5px solid ${CHORD.border}`, padding:'1rem 2rem' }}>
          <div style={{ display:'flex', gap:8, marginBottom:'0.75rem', alignItems:'center' }}>
            <span style={{ fontSize:11, color:'rgba(245,235,221,0.4)' }}>Toggle columns:</span>
            <button onClick={showAllCols} style={{ background:'none', border:`0.5px solid ${CHORD.border}`, borderRadius:5, padding:'3px 10px', fontSize:11, color:'rgba(245,235,221,0.5)', cursor:'pointer', fontFamily:'inherit' }}>Show all</button>
            <button onClick={resetColsDefault} style={{ background:'none', border:`0.5px solid ${CHORD.border}`, borderRadius:5, padding:'3px 10px', fontSize:11, color:'rgba(245,235,221,0.5)', cursor:'pointer', fontFamily:'inherit' }}>Reset defaults</button>
          </div>
          {['basic','location','metrics','rates','collab'].map(group => (
            <div key={group} style={{ marginBottom:'0.6rem' }}>
              <div style={{ fontSize:10, color:'rgba(245,235,221,0.3)', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>{group}</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {ALL_COLUMNS.filter(c=>c.group===group).map(col => (
                  <button key={col.key} onClick={()=>toggleCol(col.key)} style={{
                    background: visibleCols.has(col.key) ? `${platformInfo.color}20` : 'rgba(245,235,221,0.04)',
                    border: `0.5px solid ${visibleCols.has(col.key) ? platformInfo.color+'60' : 'rgba(245,235,221,0.1)'}`,
                    borderRadius:20, padding:'3px 12px', fontSize:11,
                    color: visibleCols.has(col.key) ? platformInfo.color : 'rgba(245,235,221,0.35)',
                    cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s',
                  }}>{col.label}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Meta */}
      <div style={{ padding:'0.6rem 2rem', fontSize:11, color:'rgba(245,235,221,0.3)', display:'flex', gap:14, flexWrap:'wrap', alignItems:'center' }}>
        {loading ? <span style={{color:platformInfo.color}}>Searching...</span> : <>
          <strong style={{color:platformInfo.color}}>{rows.length}</strong> influencer{rows.length!==1?'s':''}
          <span style={{color:'rgba(29,158,117,0.5)'}}>■ auto-synced</span>
          <span style={{color:`rgba(242,149,27,0.5)`}}>■ rates</span>
          <span style={{color:'rgba(245,235,221,0.2)'}}>■ editable — click any cell to edit</span>
        </>}
      </div>

      {error && <div style={{padding:'0 2rem 1rem',color:'#E24B4A',fontSize:13}}>{error}</div>}

      {/* Import result */}
      {importResult && (
        <div style={{ margin:'0 2rem 1rem', padding:'12px 16px', borderRadius:10, background: importResult.error ? 'rgba(229,93,74,0.1)' : 'rgba(29,158,117,0.1)', border:`0.5px solid ${importResult.error ? 'rgba(229,93,74,0.3)' : 'rgba(29,158,117,0.3)'}` }}>
          {importResult.error ? (
            <div style={{ fontSize:13, color:'#E55D4A' }}>⚠ Import failed: {importResult.error}</div>
          ) : (
            <>
              <div style={{ fontSize:13, color:'#1D9E75', fontWeight:600, marginBottom:4 }}>
                ✓ Import complete — {importResult.added} added, {importResult.skipped} skipped out of {importResult.total} rows
              </div>
              {importResult.errors?.length > 0 && (
                <div style={{ fontSize:11, color:'rgba(245,235,221,0.5)', marginTop:6 }}>
                  {importResult.errors.slice(0,5).map((e,i) => <div key={i}>• {e}</div>)}
                  {importResult.errors.length > 5 && <div>...and {importResult.errors.length-5} more</div>}
                </div>
              )}
            </>
          )}
          <button onClick={()=>setImportResult(null)} style={{ fontSize:11, color:'rgba(245,235,221,0.4)', background:'none', border:'none', cursor:'pointer', marginTop:6, fontFamily:'inherit' }}>Dismiss</button>
        </div>
      )}

      {/* Duplicate confirmation modal */}
      {importModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem' }}>
          <div style={{ background:'#1F1F1B', border:`0.5px solid ${CHORD.border}`, borderRadius:16, width:'100%', maxWidth:540, padding:'1.5rem' }}>
            <div style={{ fontSize:16, fontWeight:700, color:CHORD.beige, marginBottom:8 }}>Duplicate Influencers Found</div>
            <div style={{ fontSize:13, color:CHORD.textMuted, marginBottom:'1rem' }}>
              {importModal.duplicates.length} influencer{importModal.duplicates.length !== 1 ? 's' : ''} in this file already exist in the database:
            </div>
            <div style={{ maxHeight:180, overflowY:'auto', marginBottom:'1.25rem', background:'rgba(245,235,221,0.03)', borderRadius:8, padding:'8px 12px' }}>
              {importModal.duplicates.map((d,i) => (
                <div key={i} style={{ fontSize:12, color:CHORD.beige, padding:'3px 0', borderBottom:`0.5px solid ${CHORD.border}` }}>
                  {d.payload.influencer_name}
                </div>
              ))}
            </div>
            <div style={{ fontSize:13, color:CHORD.beige, marginBottom:'1rem', fontWeight:600 }}>What would you like to do with duplicates?</div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <button onClick={() => { const {duplicates, newRows} = importModal; runImport(newRows, duplicates, 'update'); }}
                style={{ background:'rgba(44,124,229,0.15)', border:`0.5px solid rgba(44,124,229,0.4)`, borderRadius:8, padding:'10px 16px', fontSize:13, color:'#2C7CE5', cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
                <strong>Update</strong> — overwrite existing data with sheet version
              </button>
              <button onClick={() => { const {duplicates, newRows} = importModal; runImport(newRows, duplicates, 'retain'); }}
                style={{ background:'rgba(29,158,117,0.1)', border:`0.5px solid rgba(29,158,117,0.3)`, borderRadius:8, padding:'10px 16px', fontSize:13, color:'#1D9E75', cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
                <strong>Retain</strong> — keep existing, only add new influencers ({importModal.newRows.length})
              </button>
              <button onClick={() => { const {duplicates, newRows} = importModal; runImport(newRows, duplicates, 'replace'); }}
                style={{ background:'rgba(229,93,74,0.1)', border:`0.5px solid rgba(229,93,74,0.3)`, borderRadius:8, padding:'10px 16px', fontSize:13, color:CHORD.coral, cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
                <strong>Replace</strong> — delete existing and use sheet version
              </button>
              <button onClick={() => setImportModal(null)}
                style={{ background:'none', border:`0.5px solid ${CHORD.border}`, borderRadius:8, padding:'8px 16px', fontSize:12, color:CHORD.textMuted, cursor:'pointer', fontFamily:'inherit' }}>
                Cancel import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table + sticky scrollbar wrapper */}
      <div style={{ position:'relative' }}>
        {/* Table */}
        <div style={{ overflowX:'scroll', overflowY:'auto', margin:'0 2rem 0', maxHeight:'calc(100vh - 280px)' }} className="sheet-scroll" ref={tableScrollRef}>
        <table style={{ width:'100%', borderCollapse:'collapse', minWidth:600 }}>
          <thead>
            <tr>
              {/* Basic editable cols */}
              {[['influencer_name','Name'],['handle','Handle'],['tier','Tier'],['niche','Niche'],['audience','Audience'],['city','City'],['state','State'],['country','Country'],['language','Language']].filter(([col])=>visibleCols.has(col)).map(([col,lbl],i)=>(
                <th key={col} style={i===0 ? thName : thEdit} className={i===0 ? 'col-name' : ''} onClick={()=>handleSort(col)}>{lbl}<SI col={col}/></th>
              ))}
              {/* Auto-synced cols */}
              {[['followers','Followers'],['avg_views','Avg Views/Post'],['engagement_rate','Eng Rate'],['reach','Avg Reach/Post'],['post_count_30d','Posts (30d)'],['video_count_30d','Videos (30d)']].filter(([col])=>visibleCols.has(col)).map(([col,lbl])=>(
                <th key={col} style={thAuto} onClick={()=>handleSort(col)}>{lbl}<SI col={col}/></th>
              ))}
              {/* Rate cols */}
              {(activeRateType?[activeRateType]:RATE_TYPES).filter(rt=>visibleCols.has(`rate_${rt}`)).map(rt=>(
                <th key={rt} style={thRate} onClick={()=>handleSort(`rate_${rt}`)}>{rt.charAt(0).toUpperCase()+rt.slice(1)} ₹<SI col={`rate_${rt}`}/></th>
              ))}
              {/* Other editable cols */}
              {[['previous_collab','Past Collabs'],['brands','Brands'],['email_contact','Contact'],['agency_manager','Agency / Manager'],['rate','Base Rate ₹'],['preferred_deliverables','Deliverables'],['brand_fit','Brand Fit'],['relationship_status','Relationship'],['notes','Notes']].filter(([col])=>visibleCols.has(col)).map(([col,lbl])=>(
                <th key={col} style={thEdit} onClick={()=>handleSort(col)}>{lbl}<SI col={col}/></th>
              ))}
              <th style={thEdit}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length===0&&!loading&&(
              <tr><td colSpan={30} style={{...td,textAlign:'center',color:'rgba(245,235,221,0.2)',padding:'3rem'}}>No influencers found</td></tr>
            )}
            {sorted.map(inf => (
              <tr key={inf.id}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(245,235,221,0.015)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}
              >
                {visibleCols.has('influencer_name') && (
                  <td style={tdName} className="col-name"
                    onClick={() => navigate(`/platform/${platform}/influencer/${inf.id}`)}
                    title="Click to view profile"
                  >
                    <span style={{ cursor: 'pointer', borderBottom: `1px dashed ${platformInfo.color}50` }}>
                      {inf.influencer_name}
                    </span>
                  </td>
                )}
                {visibleCols.has('handle') && <EditableCell {...ec('handle',inf,tdM)}>
                  {inf.handle?<a href={inf.handle} target="_blank" rel="noreferrer" style={{color:platformInfo.color,textDecoration:'none',fontSize:11}} onClick={e=>e.stopPropagation()}>{inf.handle.replace('https://','').replace('www.','').substring(0,25)}</a>:null}
                </EditableCell>}
                {visibleCols.has('tier') && <SelectCell value={inf.tier} field="tier" infId={inf.id} platform={platform} onSaved={handleCellSaved} options={TIERS} tdStyle={td}
                  renderValue={v=><span style={{background:`${TIER_COLORS[v]}22`,color:TIER_COLORS[v],borderRadius:20,padding:'2px 8px',fontSize:10,fontWeight:600}}>{v}</span>} />}
                {visibleCols.has('niche') && <SelectCell value={inf.niche} field="niche" infId={inf.id} platform={platform} onSaved={handleCellSaved} options={NICHES} tdStyle={tdM} />}
                {visibleCols.has('audience') && <EditableCell {...ec('audience',inf,tdM)}>{inf.audience}</EditableCell>}
                {visibleCols.has('city') && <EditableCell {...ec('city',inf,tdM)}><Pills value={inf.city} color="#85B7EB"/></EditableCell>}
                {visibleCols.has('state') && <EditableCell {...ec('state',inf,tdM)}>{inf.state}</EditableCell>}
                {visibleCols.has('country') && <EditableCell {...ec('country',inf,tdM)}>{inf.country}</EditableCell>}
                {visibleCols.has('language') && <EditableCell {...ec('language',inf,tdM)}><Pills value={inf.language} color="#7F77DD"/></EditableCell>}
                {visibleCols.has('followers') && <EditableCell {...ec('followers',inf,{...tdAuto,fontWeight:500})} isAuto>{fmtK(inf.followers)}</EditableCell>}
                {visibleCols.has('avg_views') && <EditableCell {...ec('avg_views',inf,tdAuto)} isAuto>{fmtK(inf.avg_views)}</EditableCell>}
                {visibleCols.has('engagement_rate') && <EditableCell {...ec('engagement_rate',inf,{...tdAuto,color:inf.engagement_rate>0.05?'#1D9E75':'rgba(245,235,221,0.7)'})} isAuto>{fmtPct(inf.engagement_rate)}</EditableCell>}
                {visibleCols.has('reach') && <EditableCell {...ec('reach',inf,tdAuto)} isAuto>{inf.reach!=null?fmtK(inf.reach):'—'}</EditableCell>}
                {visibleCols.has('post_count_30d') && <EditableCell {...ec('post_count_30d',inf,tdAuto)} isAuto>{inf.post_count_30d!=null?inf.post_count_30d:'—'}</EditableCell>}
                {visibleCols.has('video_count_30d') && <EditableCell {...ec('video_count_30d',inf,tdAuto)} isAuto>{inf.video_count_30d!=null?inf.video_count_30d:'—'}</EditableCell>}
                {(activeRateType?[activeRateType]:RATE_TYPES).filter(rt=>visibleCols.has(`rate_${rt}`)).map(rt=>(
                  <EditableCell key={rt} {...ec(`rate_${rt}`,inf,tdRate)}>
                    {inf[`rate_${rt}`]?'₹'+Number(inf[`rate_${rt}`]).toLocaleString('en-IN'):null}
                  </EditableCell>
                ))}
                {visibleCols.has('previous_collab') && <EditableCell {...ec('previous_collab',inf,tdM)}><Pills value={inf.previous_collab} color={CHORD.orange}/></EditableCell>}
                {visibleCols.has('brands')          && <EditableCell {...ec('brands',inf,tdM)}><Pills value={inf.brands} color={CHORD.azure || '#2C7CE5'}/></EditableCell>}
                {visibleCols.has('email_contact') && <EditableCell {...ec('email_contact',inf,tdM)}>{inf.email_contact}</EditableCell>}
                {visibleCols.has('agency_manager') && <EditableCell {...ec('agency_manager',inf,tdM)}>{inf.agency_manager}</EditableCell>}
                {visibleCols.has('rate') && <EditableCell {...ec('rate',inf,{...td,color:CHORD.orange})}>{fmtRate(inf.rate)}</EditableCell>}
                {visibleCols.has('preferred_deliverables') && <EditableCell {...ec('preferred_deliverables',inf,tdM)}><Pills value={inf.preferred_deliverables} color="#ED93B1"/></EditableCell>}
                {visibleCols.has('brand_fit') && <SelectCell value={inf.brand_fit} field="brand_fit" infId={inf.id} platform={platform} onSaved={handleCellSaved} options={['Y','N']} tdStyle={td}
                  renderValue={v=>v==='Y'?<span style={{color:'#1D9E75',fontWeight:600}}>Y</span>:v==='N'?<span style={{color:'#E24B4A',fontWeight:600}}>N</span>:null} />}
                {visibleCols.has('relationship_status') && <EditableCell {...ec('relationship_status',inf,tdM)}>{inf.relationship_status}</EditableCell>}
                {visibleCols.has('notes') && <EditableCell {...ec('notes',inf,{...tdM,maxWidth:160})}>{inf.notes}</EditableCell>}
                <td style={td}>
                  <div style={{display:'flex',gap:4}}>
                    {platform==='ig'&&<>
                      <button onClick={()=>handleGenerateLink(inf)} style={{fontSize:10,padding:'3px 7px',borderRadius:4,cursor:'pointer',border:'none',background:'rgba(225,48,108,0.15)',color:'#E1306C',fontFamily:'inherit'}}>🔗</button>
                      {inf.ig_access_token&&<button onClick={()=>handleFetchIG(inf)} style={{fontSize:10,padding:'3px 7px',borderRadius:4,cursor:'pointer',border:'none',background:'rgba(29,158,117,0.15)',color:'#1D9E75',fontFamily:'inherit'}}>↻</button>}
                    </>}
                    {getUser()?.role==='admin'&&<button onClick={()=>handleDelete(inf.id,inf.influencer_name)} style={{fontSize:10,padding:'3px 7px',borderRadius:4,cursor:'pointer',border:'none',background:'rgba(226,75,74,0.12)',color:'#E24B4A',fontFamily:'inherit'}}>✕</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Sticky scrollbar */}
      <StickyScrollBar tableRef={tableScrollRef} />
      </div>
    </div>
  );
}
